<template>
  <v-container max-width="100%" style="margin-top: 2%">
    <v-card class="elevation-7" style="border-radius: 10px;">
      <v-card-title class="subtitle-1">
        {{ $t('relatoriosExtenso.title') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('relatoriosExtenso.subtitle') }}
      </v-card-subtitle>
      <v-divider />
      <v-card-text>
        <v-form ref="form" v-model="formValidado">
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="relatorioId"
                :items="relatorioOptions"
                item-text="text"
                item-value="value"
                :label="$t('relatoriosExtenso.report')"
                :rules="[(v) => !!v || $t('relatoriosExtenso.required')]"
                :no-data-text="$t('relatoriosExtenso.noReports')"
                clearable
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="formato"
                :items="formatoOptions"
                item-text="text"
                item-value="value"
                :label="$t('relatoriosExtenso.format')"
                :rules="[(v) => !!v || $t('relatoriosExtenso.required')]"
                outlined
                dense
              />
            </v-col>
            <v-col v-if="showGrupo" cols="12" md="3">
              <v-select
                v-model="grupo"
                :items="grupoOptions"
                item-text="text"
                item-value="value"
                :label="$t('relatoriosExtenso.groupBy')"
                outlined
                dense
              />
            </v-col>
            <v-col v-if="showTableVariant" cols="12" md="6">
              <v-select
                v-model="tableVariant"
                :items="tableVariantOptions"
                item-text="text"
                item-value="value"
                :label="$t('relatoriosExtenso.table')"
                :rules="[(v) => !!v || $t('relatoriosExtenso.required')]"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row v-if="relatorioId && showDates">
            <v-col cols="12" sm="6" md="4">
              <v-datetime-picker
                :label="$t('relatoriosExtenso.startDate')"
                v-model="dti"
                :time-picker-props="timeProps"
                :text-field-props="textFieldProps"
                time-format="HH:mm:ss"
                date-format="dd/MM/yyyy"
                :clear-text="$t('datetime.close')"
              >
                <template slot="dateIcon">
                  <v-icon>mdi-calendar</v-icon>
                </template>
                <template slot="timeIcon">
                  <v-icon>mdi-clock</v-icon>
                </template>
              </v-datetime-picker>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-datetime-picker
                :label="$t('relatoriosExtenso.endDate')"
                v-model="dtf"
                :time-picker-props="timeProps"
                :text-field-props="textFieldProps"
                time-format="HH:mm:ss"
                date-format="dd/MM/yyyy"
                :clear-text="$t('datetime.close')"
              >
                <template slot="dateIcon">
                  <v-icon>mdi-calendar</v-icon>
                </template>
                <template slot="timeIcon">
                  <v-icon>mdi-clock</v-icon>
                </template>
              </v-datetime-picker>
            </v-col>
            <v-col v-if="showIntervalo" cols="12" sm="6" md="4">
              <v-select
                v-model="intervalo"
                :items="intervaloOptions"
                item-text="text"
                item-value="value"
                :label="$t('relTripPassangers.interval')"
                :rules="requireIntervalo ? [(v) => !!v || $t('relatoriosExtenso.required')] : []"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row v-if="showAnyEntityFilter || showCerca">
            <v-col v-if="showEmpresa" cols="12" sm="6" md="3">
              <v-select
                :items="empresas"
                v-model="empresaSelecionada"
                item-text="razao_social"
                item-value="id_empresa"
                :label="$t('routs.headers.company')"
                clearable
                outlined
                dense
              />
            </v-col>
            <v-col v-if="showCerca" cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="cercaSelecionada"
                :items="cercas"
                item-text="nome_cerca"
                item-value="id_cerca"
                :label="$t('nav.registration.geofences')"
                :rules="[(v) => !!v || $t('relatoriosExtenso.required')]"
                clearable
                outlined
                dense
              />
            </v-col>
            <v-col v-if="showVeiculos" cols="12" sm="6" md="3">
              <MultiplaEscolha
                lazy
                get-url="getVeiculoslazy"
                v-bind:selected-items.sync="veiculosSelecionados"
                text="cod_veiculo"
                value="id_veiculo"
                :label="$t('nav.registration.vehicles')"
                :max-items="2"
                :rules="requireVeiculos ? [(v) => (v && v.length) || $t('relatoriosExtenso.required')] : []"
              />
            </v-col>
            <v-col v-if="showLinhas" cols="12" sm="6" md="3">
              <MultiplaEscolha
                lazy
                get-url="getLinhaslazy"
                v-bind:selected-items.sync="linhasSelecionadas"
                text="nome_linha"
                value="id_linha"
                :label="$t('nav.registration.lines')"
                :max-items="2"
                :rules="requireLinhas ? [(v) => (v && v.length) || $t('relatoriosExtenso.required')] : []"
              />
            </v-col>
            <v-col v-if="showUnidades" cols="12" sm="6" md="3">
              <MultiplaEscolha
                lazy
                get-url="getUnidadeslazy"
                v-bind:selected-items.sync="unidadesSelecionadas"
                text="descricao_unidade"
                value="id_unidade"
                :label="$t('nav.registration.units')"
                :max-items="2"
              />
            </v-col>
          </v-row>
        </v-form>

        <v-alert dense text type="info" class="mt-2 mb-0">
          <div class="d-flex flex-wrap align-center">
            <span>{{ $t('relatoriosExtenso.hint') }}</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  class="ml-2"
                  :to="{ path: '/solicitacoesrelatorios' }"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('nav.reportRequests') }}</span>
            </v-tooltip>
          </div>
        </v-alert>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text color="primary" :disabled="submitting" @click="resetForm">
          {{ $t('button.clear') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="solicitar"
        >
          {{ $t('relatoriosExtenso.request') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import MultiplaEscolha from './Selects/MultiplaEscolha.vue';
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import DateUtils from '@/utils/dateutils';
import { buildRelatorioExportPayload } from '@/utils/relatorioExportProfiles';
import {
  RELATORIOS_EXTENSO_CATALOG,
  getRelatorioExtensoById,
  getRelatorioExtensoTableVariant,
} from '@/utils/relatoriosExtensoCatalog';
import {
  addPendingSchedulerDownload,
  SCHEDULER_DOWNLOAD_TYPES,
} from '@/utils/schedulerDownloadPending';

const GRUPOS = ['veiculo', 'linha', 'unidade', 'empresa', 'cliente'];

export default {
  name: 'RelatoriosExtenso',
  components: { MultiplaEscolha },
  data() {
    return {
      formValidado: false,
      submitting: false,
      relatorioId: null,
      formato: 'csv',
      grupo: 'veiculo',
      tableVariant: null,
      intervalo: 'DAY',
      dti: null,
      dtf: null,
      empresas: [],
      empresaSelecionada: null,
      veiculosSelecionados: [],
      linhasSelecionadas: [],
      unidadesSelecionadas: [],
      cercas: [],
      cercaSelecionada: null,
      linhasCatalog: [],
      config: null,
      timeProps: { format: '24hr', useSeconds: true },
      textFieldProps: { prependIcon: '', rules: [(v) => !!v || ''] },
    };
  },
  computed: {
    permissoesTelas() {
      const raw = sessionStorage.getItem('permissoes');
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_err) {
        return [];
      }
    },
    telasPermitidas() {
      const user = (() => {
        try {
          return JSON.parse(sessionStorage.getItem('user') || 'null');
        } catch (_err) {
          return null;
        }
      })();
      if (user && (user.is_superadmin || user.isSuperadmin)) {
        return null; // null = todas
      }
      return new Set(
        this.permissoesTelas
          .map((p) => p && p.nome_tela)
          .filter(Boolean)
      );
    },
    relatorioOptions() {
      const allowed = this.telasPermitidas;
      return RELATORIOS_EXTENSO_CATALOG
        .filter((entry) => !allowed || allowed.has(entry.screen))
        .map((entry) => ({
          value: entry.id,
          text: this.$t(entry.labelKey),
        }))
        .sort((a, b) => String(a.text).localeCompare(String(b.text), this.$i18n.locale));
    },
    formatoOptions() {
      return [
        { value: 'csv', text: 'CSV' },
        { value: 'pdf', text: 'PDF' },
      ];
    },
    grupoOptions() {
      return GRUPOS.map((g) => ({
        value: g,
        text: this.$t(`relPontualidade.tabs.${g === 'veiculo' ? 'vehicle' : g === 'linha' ? 'line' : g === 'unidade' ? 'unit' : g === 'empresa' ? 'company' : 'client'}`),
      }));
    },
    selectedEntry() {
      return getRelatorioExtensoById(this.relatorioId);
    },
    entryFilters() {
      return (this.selectedEntry && this.selectedEntry.filters) || [];
    },
    showEmpresa() {
      return this.entryFilters.includes('empresa');
    },
    showLinhas() {
      return this.entryFilters.includes('linhas');
    },
    showVeiculos() {
      return this.entryFilters.includes('veiculos');
    },
    showUnidades() {
      return this.entryFilters.includes('unidades');
    },
    showGrupo() {
      return this.entryFilters.includes('grupo');
    },
    showTableVariant() {
      return !!(this.selectedEntry && Array.isArray(this.selectedEntry.tableVariants) && this.selectedEntry.tableVariants.length);
    },
    tableVariantOptions() {
      const variants = (this.selectedEntry && this.selectedEntry.tableVariants) || [];
      return variants.map((variant) => ({
        value: variant.id,
        text: this.$t(variant.labelKey),
      }));
    },
    showCerca() {
      return this.entryFilters.includes('cerca');
    },
    requireLinhas() {
      return ((this.selectedEntry && this.selectedEntry.requiredFilters) || []).includes('linhas');
    },
    requireVeiculos() {
      return ((this.selectedEntry && this.selectedEntry.requiredFilters) || []).includes('veiculos');
    },
    showDates() {
      const entry = this.selectedEntry;
      if (!entry) return false;
      return entry.needsDates !== false;
    },
    showIntervalo() {
      return this.entryFilters.includes('intervalo') && this.isPassageirosFaixasVariant;
    },
    requireIntervalo() {
      return this.showIntervalo;
    },
    isPassageirosFaixasVariant() {
      if (!this.showTableVariant) return this.entryFilters.includes('intervalo');
      return !this.tableVariant || this.tableVariant === 'faixas';
    },
    intervaloOptions() {
      return [
        { text: this.$t('datetime.hour'), value: 'HOUR' },
        { text: this.$t('datetime.day'), value: 'DAY' },
        { text: this.$t('datetime.month'), value: 'MONTH' },
        { text: this.$t('datetime.year'), value: 'YEAR' },
      ];
    },
    showAnyEntityFilter() {
      return this.showEmpresa || this.showLinhas || this.showVeiculos || this.showUnidades;
    },
    canSubmit() {
      if (!this.relatorioId || !this.formato || this.submitting) return false;
      if (this.showDates && (!this.dti || !this.dtf)) return false;
      if (this.showCerca && !this.cercaSelecionada) return false;
      if (this.requireLinhas && !this.linhasSelecionadas.length) return false;
      if (this.requireVeiculos && !this.veiculosSelecionados.length) return false;
      if (this.showTableVariant && !this.tableVariant) return false;
      if (this.requireIntervalo && !this.intervalo) return false;
      return true;
    },
  },
  watch: {
    relatorioId() {
      const variant = getRelatorioExtensoTableVariant(this.selectedEntry, null);
      this.tableVariant = variant ? variant.id : null;
      this.intervalo = 'DAY';
    },
  },
  created() {
    this.loadEmpresas();
    this.loadCercas();
    this.loadConfig();
    this.loadLinhasCatalog();
  },
  methods: {
    loadConfig() {
      apiController.request(apiController.endpoints.getallConfig, {
        silent: true,
        onSuccess: (response) => {
          this.config = response.data && response.data[0];
          if (this.config) {
            [this.dti, this.dtf] = DateUtils.setDefaultDateFilters(
              this.config.hora_inicial_dia_operacao
            );
          }
        },
      });
    },
    loadEmpresas() {
      apiController.request(apiController.endpoints.getallEmpresas, {
        silent: true,
        onSuccess: (response) => {
          this.empresas = response.data || [];
        },
      });
    },
    loadCercas() {
      apiController.request(apiController.endpoints.getallCercasativascomtipo, {
        silent: true,
        onSuccess: (response) => {
          this.cercas = response.data || [];
        },
      });
    },
    loadLinhasCatalog() {
      apiController.request(apiController.endpoints.getLinhasativas, {
        silent: true,
        onSuccess: (response) => {
          this.linhasCatalog = response.data || [];
        },
      });
    },
    resetForm() {
      this.relatorioId = null;
      this.formato = 'csv';
      this.grupo = 'veiculo';
      this.tableVariant = null;
      this.intervalo = 'DAY';
      this.empresaSelecionada = null;
      this.cercaSelecionada = null;
      this.veiculosSelecionados = [];
      this.linhasSelecionadas = [];
      this.unidadesSelecionadas = [];
      if (this.config) {
        [this.dti, this.dtf] = DateUtils.setDefaultDateFilters(
          this.config.hora_inicial_dia_operacao
        );
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async solicitar() {
      if (!this.canSubmit) return;
      const entry = this.selectedEntry;
      if (!entry) return;

      this.submitting = true;
      try {
        const user = (() => {
          try {
            return JSON.parse(sessionStorage.getItem('user') || 'null');
          } catch (_err) {
            return null;
          }
        })();

        const data = await buildRelatorioExportPayload(entry, {
          dtiSql: this.showDates && this.dti ? DateUtils.getDateSQLFormatted(this.dti) : null,
          dtfSql: this.showDates && this.dtf ? DateUtils.getDateSQLFormatted(this.dtf) : null,
          empresa: this.empresaSelecionada,
          linhas: this.linhasSelecionadas,
          veiculos: this.veiculosSelecionados,
          unidades: this.unidadesSelecionadas,
          grupo: this.grupo,
          cerca: this.cercaSelecionada,
          format: this.formato,
          token: sessionStorage.getItem('api'),
          user,
          linhasCatalog: this.linhasCatalog,
          empresas: this.empresas,
          config: this.config,
          tableVariant: this.tableVariant,
          intervalo: this.intervalo,
          t: (key, ...args) => this.$t(key, ...args),
        });

        const response = await apiController.request(
          apiController.endpoints.exportTableCsv,
          { data, silent: true }
        );
        const body = (response && response.data) || {};
        if (body.erro || !body.id_processo || body.status !== 'PENDING') {
          throw new Error(body.mensagem || 'export failed');
        }

        const downloadType =
          this.formato === 'pdf'
            ? SCHEDULER_DOWNLOAD_TYPES.TABLE_PDF_EXPORT
            : SCHEDULER_DOWNLOAD_TYPES.TABLE_CSV_EXPORT;

        addPendingSchedulerDownload({
          id_processo: body.id_processo,
          type: downloadType,
          meta: { filename: data.filename, format: this.formato },
        });
        EventBus.$emit('scheduler-download-enqueued');
        EventBus.$emit('show-alert', {
          mensagem: this.$t('relatoriosExtenso.enqueued'),
          tipo: 'info',
          duracao: 6000,
        });
      } catch (_err) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('relatoriosExtenso.failed'),
          tipo: 'error',
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
